/**
 * Identité et coordonnées publiques de RLCore — SOURCE UNIQUE.
 *
 * Ces valeurs alimentent à la fois les données structurées (schema.org), le
 * pied de page, la page contact et les mentions légales. Google recoupe ces
 * informations avec la fiche Google Business Profile et les annuaires : la
 * moindre divergence (un « Av. » ici, un « Avenue » là, deux numéros de
 * téléphone différents) affaiblit le référencement local. D'où ce fichier
 * unique — ne JAMAIS réécrire une adresse ou un téléphone en dur ailleurs.
 *
 * ⚠️ Les champs marqués À FOURNIR portent encore des valeurs de remplacement.
 * Tant qu'ils ne sont pas renseignés, ils sont automatiquement OMIS des
 * données structurées (voir `hasPostalAddress` / `hasPhone` plus bas) :
 * publier une adresse fictive vaut bien pire que ne pas en publier.
 */

/**
 * Raison sociale, telle que déclarée (SIREN 992 594 002). Entreprise
 * individuelle : l'exploitant est OWNER_NAME, « RLCore » est l'enseigne.
 * Lus par les données structurées (`legalName`) et les pages légales.
 */
export const LEGAL_NAME = "LAUPIES EI";
export const OWNER_NAME = "Raphaël Laupies";
export const LEGAL_FORM = "Entrepreneur individuel (micro-entreprise)";
export const RCS = "RCS Toulon 992 594 002";
export const VAT_MENTION = "TVA non applicable, art. 293 B du CGI";

/** Date de création au registre (SIRENE). */
export const FOUNDING_DATE = "2025-10-15";

/** Nom commercial affiché. */
export const BRAND_NAME = "RLCore";

/** SIRET — identifiant légal, repris des mentions légales. */
export const SIRET = "99259400200014";
/** SIRET lisible : « 992 594 002 00014 ». */
export const SIRET_DISPLAY = SIRET.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, "$1 $2 $3 $4");

/* ── Adresse ─────────────────────────────────────────────────────────────
   Volontairement sans rue ni code postal : l'activité n'accueille pas de
   public à une adresse, et les mentions légales n'ont jamais publié que
   « Toulon, France ». Les données structurées déclarent donc la ville et la
   région, sans voie — ce qui est valide et honnête.

   À renseigner UNIQUEMENT le jour où une adresse professionnelle est reçue et
   publiée partout à l'identique (fiche Google, annuaires, mentions légales).
   Pour la fiche Google Business, l'adresse est demandée à la validation mais
   reste masquée en mode « zone de service » : la publier ici n'est pas requis. */
export const STREET_ADDRESS = ""; // ex. "12 avenue de la République"
export const POSTAL_CODE = ""; // ex. "83000"
export const CITY = "Toulon";
export const REGION = "Provence-Alpes-Côte d'Azur";
export const COUNTRY = "FR";

/* ── Contacts ────────────────────────────────────────────────────────────
   DEUX numéros, deux usages qui ne doivent pas se mélanger :

   • TELEPHONE (français) est l'identité publique. Il alimente les données
     structurées, le pied de page et la page contact — donc tout ce que Google
     recoupe avec la fiche Business et les annuaires. Il doit y être écrit à
     l'identique, au caractère près.

   • WHATSAPP_NUMBER (dominicain) ne sert QU'au marché hispanophone, et n'est
     utilisé que par le bouton WhatsApp de la page /republica-dominicana. Le
     faire remonter dans les données structurées créerait une incohérence avec
     la fiche Google française, qui affaiblirait le référencement local. */
export const TELEPHONE = "+33782707997";
/** Chiffres uniquement, indicatif pays compris (wa.me n'accepte pas le « + »). */
export const WHATSAPP_NUMBER = "18093664485";
export const EMAIL = "contact@rlcore.fr";

/**
 * Fondateur — la personne derrière l'entité : l'exploitant de l'EI, nom tel
 * qu'il figure sur les mentions légales.
 * Sert au `founder` du ProfessionalService et à l'`author` des articles :
 * Google veut une personne identifiable derrière un contenu, pas une marque.
 */
export const FOUNDER_NAME = OWNER_NAME;
export const FOUNDER_ID_SUFFIX = "#founder";

/* ── Géolocalisation ─────────────────────────────────────────────────────
   Coordonnées du centre de Toulon. À affiner avec l'adresse réelle une fois
   connue — elles servent au rayon de recherche local. */
export const GEO = { latitude: 43.1242, longitude: 5.928 } as const;

/**
 * Profils officiels — réels et actifs, avec leur libellé.
 *
 * Une seule liste pour trois usages : le pied de page du site, les données
 * structurées (`sameAs`, qui permet à Google de relier le site aux profils et
 * de consolider l'entité), et le pied de page des e-mails de prospection.
 * Ne mettre QUE des profils existants : un lien mort coûte de la crédibilité
 * et n'apporte aucun signal aux moteurs.
 *
 * URLs CANONIQUES, sans paramètre de suivi ni de langue : « ?locale=es_ES »
 * traîné depuis un partage ferait servir la page en espagnol à tout le monde.
 */
export const PROFILS_SOCIAUX = [
  { label: "Instagram", url: "https://www.instagram.com/rlcore.fr/" },
  { label: "Facebook", url: "https://www.facebook.com/profile.php?id=61593153204314" },
  { label: "LinkedIn", url: "https://www.linkedin.com/company/rlcore-fr/" },
] as const;

/** Les mêmes, à plat, pour schema.org et le pied de page du site. */
export const SAME_AS: string[] = PROFILS_SOCIAUX.map((p) => p.url);

/** Fourchette tarifaire, notation schema.org (€ à €€€€). */
export const PRICE_RANGE = "€€";

/** Horaires d'ouverture — repris tels quels sur la page contact. */
export const OPENING_HOURS = {
  days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  opens: "09:00",
  closes: "18:00",
} as const;

/**
 * Zones desservies. Les communes sont celles de la fiche Google Business : le
 * site et la fiche doivent dire la même chose. La République dominicaine n'y
 * figure plus — elle diluait le signal varois de l'entité principale ; elle est
 * déclarée sur sa propre page (/republica-dominicana).
 */
export const AREA_SERVED = [
  { type: "City", name: "Toulon" },
  { type: "City", name: "La Garde" },
  { type: "City", name: "La Valette-du-Var" },
  { type: "City", name: "La Seyne-sur-Mer" },
  { type: "City", name: "Six-Fours-les-Plages" },
  { type: "City", name: "Sanary-sur-Mer" },
  { type: "City", name: "Hyères" },
  { type: "AdministrativeArea", name: "Var" },
  { type: "AdministrativeArea", name: "Provence-Alpes-Côte d'Azur" },
  { type: "Country", name: "France" },
] as const;

/* ── Dérivés ─────────────────────────────────────────────────────────────── */

/** L'adresse postale est-elle complète et publiable ? */
export const hasPostalAddress = Boolean(STREET_ADDRESS && POSTAL_CODE);

/** Un téléphone est-il renseigné ? */
export const hasPhone = Boolean(TELEPHONE);

/** Adresse sur une ligne, pour le pied de page et la page contact. */
export function formatAddress(): string {
  return hasPostalAddress
    ? `${STREET_ADDRESS}, ${POSTAL_CODE} ${CITY}, France`
    : `${CITY}, France`;
}

/** Téléphone au format lisible français (+33 6 12 34 56 78). */
export function formatPhone(): string {
  if (!hasPhone) return "";
  const national = TELEPHONE.replace(/^\+33/, "0").replace(/\D/g, "");
  return national.length === 10
    ? national.replace(/(\d{2})(?=\d)/g, "$1 ").trim()
    : TELEPHONE;
}

/** Bloc `PostalAddress` schema.org, réduit à ce qui est réellement connu. */
export function postalAddressSchema() {
  return {
    "@type": "PostalAddress",
    ...(hasPostalAddress
      ? { streetAddress: STREET_ADDRESS, postalCode: POSTAL_CODE }
      : {}),
    addressLocality: CITY,
    addressRegion: REGION,
    addressCountry: COUNTRY,
  };
}

/** Bloc `areaServed` schema.org. */
export function areaServedSchema() {
  return AREA_SERVED.map((area) => ({ "@type": area.type, name: area.name }));
}

/** Bloc `OpeningHoursSpecification` schema.org. */
export function openingHoursSchema() {
  return {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [...OPENING_HOURS.days],
    opens: OPENING_HOURS.opens,
    closes: OPENING_HOURS.closes,
  };
}
